import { memo, useCallback, useMemo } from 'react';
import { useDeleteMetaIntegration } from '../../../api/delete-integration-meta.api';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { queryClient } from '@/lib/react-query';
import { GET_LIST_INTEGRATION_META_QUERY_KEY } from '../../../api/get-list-integration-meta.api';
import { useChannelModalRootActionsStore } from '@/features/channels/_shared/components/channel-modal-root/channel-modal-root.store';
import ModalWithConfirmation from '@/components/_ui/modal-with-confirmation/modal-with-confirmation';

export type TDeleteIntegrationMetaModalType = 'DeleteIntegrationMetaModal';
export interface IDeleteIntegrationMetaModalProps {
  organizationChannelId: string;
}

const TOAST_MESSAGES_DELETE_INTEGRATION_META = {
  success: 'The channel integration has been successfully deleted!',
  error: 'Failed to delete meta integration!',
};

const DeleteIntegrationMetaModal = memo(({ organizationChannelId }: IDeleteIntegrationMetaModalProps) => {
  const channelModalRootActions = useChannelModalRootActionsStore();
  const showToast = useCustomToast();

  const isOpenDeleteIntegrationMetaModal = useMemo(() => {
    return channelModalRootActions.checkIsOpenModal('DeleteIntegrationMetaModal');
  }, [channelModalRootActions]);

  const mutationDeleteIntegrationMeta = useDeleteMetaIntegration({
    mutationConfig: {
      onError: (error) => {
        const errorMessage = axiosExtractErrorMessage(error) || TOAST_MESSAGES_DELETE_INTEGRATION_META.error;
        showToast({ type: 'error', message: errorMessage });
      },
      onSuccess: () => {
        Promise.all([queryClient.invalidateQueries({ queryKey: [GET_LIST_INTEGRATION_META_QUERY_KEY] })]);
        channelModalRootActions.closeModal('DeleteIntegrationMetaModal');
        showToast({ type: 'success', message: TOAST_MESSAGES_DELETE_INTEGRATION_META.success });
      },
    },
  });

  const handleDelete = useCallback(() => {
    if (organizationChannelId) {
      mutationDeleteIntegrationMeta.mutate({
        organizationChannelId: organizationChannelId || '',
      });
    }
  }, [organizationChannelId, mutationDeleteIntegrationMeta]);

  const handleClose = useCallback(() => {
    channelModalRootActions.closeModal('DeleteIntegrationMetaModal');
  }, [channelModalRootActions]);

  return (
    <ModalWithConfirmation
      isOpen={isOpenDeleteIntegrationMetaModal}
      onClose={handleClose}
      onDelete={handleDelete}
      isLoading={mutationDeleteIntegrationMeta.isPending}
      title="Delete Integration Meta"
      description="Do you want to delete this integration?"
      bannerTitle="Deleting this channel will permanently delete all related data, including chat history and analytics."
      confirmText="delete my channel"
    />
  );
});

DeleteIntegrationMetaModal.displayName = 'DeleteIntegrationMetaModal';

export default DeleteIntegrationMetaModal;
